import { useMemo } from "react";

import { DG3_QUERY, DG2_QUERY, GRID_QUERY } from "../resources/settings";
import sortAlphabetically from "../utils/sortAlphabetically";
import {
  explodeMetrics,
  groupByTimestamp,
  mergeSameTimestampData
} from "../utils/transformMetrics";

import useMetrics from "./useMetrics";
import useMetricsLinkData from "./useMetricsLinkData";
import useMetricsMaxDuration from "./useMetricsMaxDuration";

const INTERVAL = 60 * 10;

const getServiceMetrics = metrics => {
  if (!metrics?.length) {
    return undefined;
  }
  const mountpoints = sortAlphabetically(
    Array.from(
      new Set(
        metrics
          .map(({ mountpoint }) => mountpoint)
          .filter(mountpoint => mountpoint != null && mountpoint !== "/tmp")
      )
    )
  ).reduce((mountpoints, mountpoint) => {
    const mountpointMetrics = groupByTimestamp(
      metrics.filter(point => point.mountpoint === mountpoint)
    ).map(mergeSameTimestampData);

    if (mountpointMetrics.length) {
      return {
        ...mountpoints,
        [mountpoint]: mountpointMetrics
      };
    }

    return mountpoints;
  }, {});
  const cpu = groupByTimestamp(
    metrics.filter(point => point.type === "cpu")
  ).map(mergeSameTimestampData);
  const memory = groupByTimestamp(
    metrics.filter(point => point.type === "memory")
  ).map(mergeSameTimestampData);

  return {
    cpu,
    memory,
    ...mountpoints
  };
};

const useService = (name: string, rangeInSeconds?: number) => {
  const { collection, type, deploymentType, href, environment } =
    useMetricsLinkData();

  // deployment type exposed on Git15; fallback to #metrics link type for previous versions of Git
  const deploymentTarget = deploymentType ?? type;

  let fields;
  switch (deploymentTarget) {
    case "dedicated":
      fields = DG2_QUERY;
      break;
    case "grid":
      fields = GRID_QUERY;
      break;
    default:
      fields = DG3_QUERY;
  }

  const query = useMemo(
    () => ({
      interval: `${INTERVAL}s`,
      fields,
      stream: {
        collection: collection,
        stream: "metrics"
      },
      filters: [{ key: "service", value: name }]
    }),
    [fields, collection, name]
  );

  const { data, isLoading, error } = useMetrics(href, query, rangeInSeconds);

  const serviceMetrics = useMemo(() => {
    if (data?.metrics?.length) {
      return getServiceMetrics(explodeMetrics(data));
    }
  }, [data]);
  const maxDuration = useMetricsMaxDuration();
  return {
    serviceMetrics,
    isLoading,
    maxDuration,
    deploymentTarget,
    metricsError: error,
    environment
  };
};

export default useService;
